import { useEffect, useState } from "react";
import {
  ApiError,
  fetchIndexCapeDetail,
  fetchIndexValuationDetail,
  fetchIndicatorCatalog,
  fetchIndicatorSeries,
  fetchInstrumentDetail,
  fetchStockCapeDetail,
  refreshInstrumentNews,
  type CapeDto,
  type IndexValuationDto,
  type IndicatorCatalogEntry,
  type InstrumentDetailDto,
  type StockCapeDto,
} from "../api";
import { IndicatorPicker, indicatorKey, type ActiveIndicatorSpec } from "../components/IndicatorPicker";
import { PriceChart, type ActiveIndicatorSeries } from "../components/PriceChart";
import { ProvenancePanel } from "../components/ProvenancePanel";
import { IndexReturnCard, IndexVolatilityCard } from "../components/IndexTrackPanel";
import { ValuationBandChart } from "../components/ValuationBandChart";
import { CapeChart } from "../components/CapeChart";
import { StockCapeChart } from "../components/StockCapeChart";
import { FieldHelp } from "../components/FieldHelp";
import { PageHelper } from "../components/PageHelper";
import { AsOfBadge } from "../components/AsOfBadge";
import { exportRowsAsCsv } from "../exportCsv";
import { sortGlyph, useSort } from "../hooks/useSort";

const DEFAULT_INDICATORS: ActiveIndicatorSpec[] = [
  { name: "SMA", params: { timeperiod: 5 } },
  { name: "SMA", params: { timeperiod: 10 } },
  { name: "SMA", params: { timeperiod: 50 } },
  { name: "RSI", params: { timeperiod: 14 } },
];
const INDICATOR_COLORS = ["#e0873a", "#2f8fd0", "#8a5cd0", "#3b3ad4", "#0e9f6e", "#df453b", "#c97a1f", "#586074"];

const TA_LABELS: Record<string, string> = {
  rsi_14: "RSI (14)",
  macd_line_12_26: "MACD line",
  macd_hist_12_26_9: "MACD hist",
  atr14_pct: "ATR (14) % of price",
  price_sma50_rat: "vs SMA-50",
  price_sma200_rat: "vs SMA-200",
  vol_20d: "Realized σ (20d)",
  beta_nifty_252: "β vs NIFTY",
};
// Ratios/ATR-% render as a signed percentage; RSI/MACD-line/beta are plain numbers.
const TA_AS_PERCENT = new Set(["atr14_pct", "price_sma50_rat", "price_sma200_rat", "vol_20d"]);

function formatTa(key: string, v: number): string {
  if (TA_AS_PERCENT.has(key)) return `${v >= 0 ? "+" : ""}${(v * 100).toFixed(1)}%`;
  return v.toFixed(2);
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function InstrumentDetailPage({
  token,
  securityId,
  onBack,
  onUnauthorized,
  onSimulate,
}: {
  token: string;
  securityId: string;
  onBack: () => void;
  onUnauthorized: () => void;
  onSimulate?: (securityId: string) => void;
}) {
  const isIndex = securityId.startsWith("IDX:");
  const [asOf] = useState(todayIso());
  const [data, setData] = useState<InstrumentDetailDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [catalog, setCatalog] = useState<IndicatorCatalogEntry[]>([]);
  const [activeIndicators, setActiveIndicators] = useState<ActiveIndicatorSpec[]>(DEFAULT_INDICATORS);
  const [indicatorSeries, setIndicatorSeries] = useState<ActiveIndicatorSeries[]>([]);
  const [newsRefreshing, setNewsRefreshing] = useState(false);
  const [newsRefreshNote, setNewsRefreshNote] = useState<string | null>(null);
  // Preview gate (2026-10-25): the deterministic templated notes are
  // already fetched with every detail response (they're free -- no LLM,
  // no cache, computed inline on each render), but stay OFF by default
  // and opt-in via this toggle until reviewed -- "preview the panel
  // first, not the default view" was explicit.
  const [showNotes, setShowNotes] = useState(false);

  function handleAuthAwareError(err: unknown): boolean {
    if (err instanceof ApiError && err.status === 401) {
      onUnauthorized();
      return true;
    }
    return false;
  }

  // ON-DEMAND single-stock news refresh (2026-09-22) -- news is no longer
  // fetched by the daily cron at all (see daily_pipeline.py's own removal
  // note: a blind whole-universe rotation was wasting NewsAPI's scarce
  // free-tier quota on stocks nobody was looking at, and could fail an
  // entire day over one bad ticker). This button spends that quota on
  // exactly the stock a real user has open right now.
  async function handleRefreshNews() {
    setNewsRefreshing(true);
    setNewsRefreshNote(null);
    try {
      const result = await refreshInstrumentNews(token, securityId);
      if (result.errors > 0) {
        setNewsRefreshNote(`refresh failed: ${result.first_error ?? "unknown error"}`);
      } else if (result.written > 0) {
        setNewsRefreshNote(`found ${result.written} new headline${result.written === 1 ? "" : "s"}`);
        fetchInstrumentDetail(token, securityId, asOf).then(setData).catch(() => {});
      } else {
        setNewsRefreshNote("no new titled coverage right now (honest empty, not an error)");
      }
    } catch (err) {
      if (!handleAuthAwareError(err)) {
        setNewsRefreshNote(err instanceof ApiError ? `${err.status}: ${err.message}` : "refresh failed");
      }
    } finally {
      setNewsRefreshing(false);
    }
  }

  useEffect(() => {
    setData(null);
    setError(null);
    fetchInstrumentDetail(token, securityId, asOf)
      .then(setData)
      .catch((err) => {
        if (!handleAuthAwareError(err)) {
          setError(err instanceof ApiError ? `${err.status}: ${err.message}` : "request failed");
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, securityId, asOf]);

  useEffect(() => {
    fetchIndicatorCatalog(token)
      .then(setCatalog)
      .catch((err) => {
        if (!handleAuthAwareError(err)) console.error(err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Fetch every active indicator's series in parallel whenever the active
  // set, security, or as-of date changes -- each is a real, separate
  // on-demand TA-Lib computation (app/api/routes/indicators.py), not a
  // client-side reimplementation.
  useEffect(() => {
    let cancelled = false;
    Promise.all(
      activeIndicators.map((spec, i) =>
        fetchIndicatorSeries(token, securityId, spec.name, spec.params, asOf).then((res) => {
          const seriesByDate = new Map(res.series.map((p) => [p.date, p.values]));
          const s: ActiveIndicatorSeries = {
            key: indicatorKey(spec),
            displayName: catalog.find((c) => c.name === spec.name)?.display_name ?? spec.name,
            color: INDICATOR_COLORS[i % INDICATOR_COLORS.length],
            overlay: res.overlay,
            outputNames: res.output_names,
            seriesByDate,
          };
          return s;
        }),
      ),
    )
      .then((results) => {
        if (!cancelled) setIndicatorSeries(results);
      })
      .catch((err) => {
        if (!handleAuthAwareError(err)) console.error(err);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, securityId, asOf, activeIndicators, catalog]);

  function addIndicator(spec: ActiveIndicatorSpec) {
    setActiveIndicators((prev) => [...prev, spec]);
  }
  function removeIndicator(key: string) {
    setActiveIndicators((prev) => prev.filter((s) => indicatorKey(s) !== key));
  }

  const fundamentalsRows = data?.fundamentals.rows ?? [];
  const { sorted: sortedFundamentals, sortKey: fundSortKey, sortDir: fundSortDir, toggleSort: toggleFundSort } = useSort(
    fundamentalsRows,
    (row, key) => (row as unknown as Record<string, unknown>)[key],
  );
  const earningsRows = data?.earnings_detail ?? [];
  const { sorted: sortedEarnings, sortKey: earnSortKey, sortDir: earnSortDir, toggleSort: toggleEarnSort } = useSort(
    earningsRows,
    (row, key) => (row as unknown as Record<string, unknown>)[key],
  );

  if (error) {
    return (
      <div>
        <div className="crumb">
          <b onClick={onBack} style={{ cursor: "pointer" }}>
            Stocks
          </b>{" "}
          › {securityId}
        </div>
        <div className="error">{error}</div>
      </div>
    );
  }

  if (!data) {
    return <div className="empty-state">Loading…</div>;
  }

  const bars = data.bars.filter((b) => b.close !== null);
  const last = bars[bars.length - 1];
  const prev = bars[bars.length - 2];
  const dayChange = last && prev && last.close !== null && prev.close !== null ? last.close - prev.close : null;
  const dayChangePct = dayChange !== null && prev?.close ? (dayChange / prev.close) * 100 : null;

  const volCtx = data.volatility_context;

  return (
    <div>
      <div className="crumb">
        <b onClick={onBack} style={{ cursor: "pointer" }}>
          Stocks
        </b>
        {data.sector_key ? ` › ${data.sector_key}` : ""} › {data.display_ticker ?? data.security_id}
      </div>

      <PageHelper>
        Everything known about this instrument: real price history with any indicator you add, the coverage signal
        with full provenance (<FieldHelp term="cross_segment_blend" />which segments contributed, and which didn't),
        raw technicals, fundamentals, news, and earnings.
      </PageHelper>

      <div className="detail-card ihead">
        <div>
          <div className="tk">{data.display_ticker ?? data.security_id}</div>
          <div className="co">
            {data.company_name ?? "—"} · {data.primary_market ?? "—"} · {data.isin ?? "—"}
          </div>
          <div className="chips">
            {data.sector_key && <span className="chip">{data.sector_key}</span>}
            {data.cap_tier && <span className="chip">{data.cap_tier}</span>}
            {data.index_groups.map((g) => (
              <span className="chip idx" key={g}>
                {g}
              </span>
            ))}
          </div>
        </div>
        {last?.close !== null && last?.close !== undefined && (
          <div style={{ marginLeft: 8 }}>
            <div className="lvl">{last.close.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div>
            {dayChange !== null && dayChangePct !== null && (
              <div className={`dchg ${dayChange >= 0 ? "pos" : "neg"}`}>
                {dayChange >= 0 ? "+" : ""}
                {dayChange.toFixed(2)} · {dayChange >= 0 ? "+" : ""}
                {dayChangePct.toFixed(2)}%
              </div>
            )}
          </div>
        )}
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 20 }}>
          <div className="ihead-trio">
            {isIndex ? (
              <div className="vc-row">
                <span className="vc-k">Drift <FieldHelp term="index_track" /></span>
                <span className="vc-v mut">neutral, by design</span>
              </div>
            ) : (
              <>
            <div className="vc-row">
                  <span className="vc-k">Drift <FieldHelp term="cross_segment_blend" /></span>
                  <span className={`vc-v ${data.coverage_return.signal !== null ? (data.coverage_return.signal >= 0 ? "pos" : "neg") : "mut"}`}>
                    {data.coverage_return.signal !== null
                      ? `${data.coverage_return.signal >= 0 ? "+" : ""}${(data.coverage_return.signal * 100).toFixed(1)}%`
                      : "no signal"}
                    {data.coverage_return.matched_horizon !== null && (
                      <span className="vc-sub"> {data.coverage_return.matched_horizon}d</span>
                    )}
                  </span>
                  {data.coverage_return.signal !== null && <AsOfBadge date={data.coverage_return.signal_as_of} reference={data.expected_as_of} />}
                </div>
                <div className="vc-row">
                  <span className="vc-k">Vol σ <FieldHelp term="rank_space_sigma" /></span>
                  <span className="vc-v">
                    {data.coverage_volatility.signal !== null ? data.coverage_volatility.signal.toFixed(4) : "no signal"}
                    <span className="vc-sub"> rank, fcst</span>
                  </span>
                  {data.coverage_volatility.signal !== null && <AsOfBadge date={data.coverage_volatility.signal_as_of} reference={data.expected_as_of} />}
                </div>
              </>
            )}
            <div className="vc-row">
              <span className="vc-k">Realized <FieldHelp term="volatility_context" /></span>
              <span className="vc-v">
                {volCtx.available && volCtx.sigma_annual !== null ? `${(volCtx.sigma_annual * 100).toFixed(1)}%` : "—"}
                <span className="vc-sub"> {volCtx.window}d</span>
              </span>
              {volCtx.available && <AsOfBadge date={volCtx.as_of_date} reference={data.expected_as_of} />}
            </div>
          </div>
          {!isIndex && (
            <button className="btn" onClick={() => onSimulate?.(securityId)} disabled={!onSimulate}>
              Simulate this →
            </button>
          )}
        </div>
        {volCtx.available && volCtx.tier_label && (
          <div className="vol-context-note">
            <span className="vol-tier-chip">{volCtx.tier_label}</span>
            {" "}· expected range over the next {volCtx.horizon_days}d: ±{volCtx.expected_move_pct?.toFixed(1)}%
            <span className="mut"> — risk/range context only, not a return prediction (see "Tested — no directional signal" in the Volatility panel below).</span>
          </div>
        )}
      </div>

      <div className="detail-card">
        <div className="detail-card-head">
          <span>
            Price · candles · indicators <span className="note">real bars, {bars.length} in window</span>
          </span>
          <AsOfBadge date={last?.date ?? null} reference={data.expected_as_of} label="price" />
        </div>
        <div className="tools">
          <IndicatorPicker catalog={catalog} active={activeIndicators} onAdd={addIndicator} onRemove={removeIndicator} />
        </div>
        <PriceChart
          bars={data.bars}
          caMarkers={data.ca_markers}
          earningsMarkers={data.earnings_markers}
          macroMarkers={data.macro_markers}
          indicators={indicatorSeries}
        />
        <div className="prov-note" style={{ marginTop: 8 }}>
          No forecast/projection cone: needs path-level simulation output (ui_spec #9, not built yet) — not faked here.
        </div>
      </div>

      {isIndex && data.index_track && (
        <div className="cols2">
          <IndexReturnCard track={data.index_track} />
          <IndexVolatilityCard track={data.index_track} />
        </div>
      )}
      {!isIndex && (
        <>
          <div className="prov-llm-note-toggle-row">
            <button className="lk" onClick={() => setShowNotes((v) => !v)}>
              {showNotes ? "Hide" : "Show"} platform-generated notes (preview)
            </button>
          </div>
          {showNotes && data.llm_note_synthesis?.text && (
            <div className="prov-llm-note" style={{ marginBottom: 10 }}>
              <div className="prov-llm-note-label">
                <span>Generated from platform figures — stock summary</span>
                {data.llm_note_synthesis.register && (
                  <span className="prov-llm-note-register">{data.llm_note_synthesis.register}</span>
                )}
              </div>
              <div className="prov-llm-note-text">{data.llm_note_synthesis.text}</div>
            </div>
          )}
          <div className="cols2">
            <ProvenancePanel coverage={data.coverage_return} outputType="return" reclassifications={data.reclassifications} onSimulate={onSimulate ? () => onSimulate(securityId) : undefined} llmNote={data.llm_note_return} showNotes={showNotes} />
            <ProvenancePanel coverage={data.coverage_volatility} outputType="volatility" reclassifications={data.reclassifications} onSimulate={onSimulate ? () => onSimulate(securityId) : undefined} llmNote={data.llm_note_volatility} showNotes={showNotes} />
          </div>
        </>
      )}

      {isIndex && <IndexValuationCharts token={token} securityId={securityId} onUnauthorized={onUnauthorized} />}
      {!isIndex && <StockCapeSection token={token} securityId={securityId} onUnauthorized={onUnauthorized} />}

      <div className={isIndex ? undefined : "cols2"}>
        <div className="detail-card">
          <div className="detail-card-head">
            <span>
              Technicals <span className="note">
                {data.technicals_source === "computed_on_demand"
                  ? "computed on demand from raw bars · no stored feature history for this security"
                  : "computed as-of-T · always available"}
              </span>
            </span>
            <AsOfBadge date={data.technicals_as_of} reference={data.expected_as_of} />
          </div>
          <div className="mgrid">
            {Object.entries(data.technicals).map(([key, v]) => (
              <div className="met" key={key}>
                <span className="k">{TA_LABELS[key] ?? key}</span>
                <span className="v">{v !== null ? formatTa(key, v) : "—"}</span>
              </div>
            ))}
          </div>
        </div>

        {!isIndex && (
          <div className="detail-card">
            <div className="detail-card-head">
              <span>
                Fundamentals <span className="note">{data.fundamentals.note}</span>
              </span>
            </div>
            {data.fundamentals.rows.length === 0 ? (
              <div className="empty-state" style={{ padding: 20 }}>
                {data.fundamentals.note.charAt(0).toUpperCase() + data.fundamentals.note.slice(1)}.
              </div>
            ) : (
              <div style={{ border: "1px solid var(--rule-strong)", borderRadius: 8 }}>
                <div className="thin-head">
                  <span className="thin-badge">{data.fundamentals.rows.length} line items</span>
                  Most recent capture-forward rows
                  <button className="btn ghost sm" style={{ marginLeft: "auto" }} onClick={() => exportRowsAsCsv(
                    fundamentalsRows as unknown as Record<string, unknown>[],
                    [{ key: "report_date", label: "Report date" }, { key: "line_item", label: "Line item" }, { key: "value", label: "Value" }],
                    `${securityId}_fundamentals.csv`,
                  )}>↓ CSV</button>
                </div>
                <table className="fin">
                  <thead>
                    <tr>
                      <th className="l clickable" onClick={() => toggleFundSort("report_date")}>Report date {sortGlyph("report_date", fundSortKey, fundSortDir)}</th>
                      <th className="l clickable" onClick={() => toggleFundSort("line_item")}>Line item {sortGlyph("line_item", fundSortKey, fundSortDir)}</th>
                      <th className="clickable" onClick={() => toggleFundSort("value")}>Value {sortGlyph("value", fundSortKey, fundSortDir)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedFundamentals.map((r, i) => (
                      <tr key={i}>
                        <td className="l">{r.report_date}</td>
                        <td className="l">{r.line_item}</td>
                        <td>{r.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      <div className={isIndex ? undefined : "cols2"}>
        <div className="detail-card">
          <div className="detail-card-head">
            <span>
              News <span className="note">news_archive · title-matched · on-demand, not cron</span>
            </span>
            <button className="btn ghost sm" onClick={handleRefreshNews} disabled={newsRefreshing}>
              {newsRefreshing ? "Checking…" : "↻ Refresh news"}
            </button>
          </div>
          {newsRefreshNote && <div className="note" style={{ padding: "4px 0" }}>{newsRefreshNote}</div>}
          {data.news.length === 0 ? (
            <div className="empty-state" style={{ padding: 20 }}>
              No news captured yet ({data.news_count} total for this security as of {data.as_of}).
            </div>
          ) : (
            <>
              {data.news.map((n, i) => (
                <div className="nitem" key={i}>
                  <span className="nd">{n.published_at.slice(0, 10)}</span>
                  <div>
                    <div style={{ fontSize: "0.84rem" }}>
                      {n.url ? (
                        <a href={n.url} target="_blank" rel="noreferrer">
                          {n.title}
                        </a>
                      ) : (
                        n.title
                      )}
                    </div>
                    <div style={{ fontSize: "0.72rem", color: "var(--ink-faint)", marginTop: 2 }}>
                      {n.source_name ?? "unknown source"}
                    </div>
                  </div>
                </div>
              ))}
              <div style={{ fontSize: "0.72rem", color: "var(--ink-faint)", fontStyle: "italic", marginTop: 8 }}>
                {data.news_count} total captured for this security as of {data.as_of}.
              </div>
            </>
          )}
        </div>

        {!isIndex && (
          <div className="detail-card">
            <div className="detail-card-head">
              <span>
                Earnings <span className="note">earnings_actuals</span>
              </span>
              {data.earnings_detail.length > 0 && (
                <button className="btn ghost sm" onClick={() => exportRowsAsCsv(
                  earningsRows as unknown as Record<string, unknown>[],
                  [
                    { key: "fiscal_period", label: "Period" },
                    { key: "reported_eps", label: "Reported EPS" },
                    { key: "analyst_eps_estimate", label: "Est. EPS" },
                    { key: "surprise_pct", label: "Surprise %" },
                  ],
                  `${securityId}_earnings.csv`,
                )}>↓ CSV</button>
              )}
            </div>
            {data.earnings_detail.length === 0 ? (
              <div className="empty-state" style={{ padding: 20 }}>
                No earnings captured yet for this security.
              </div>
            ) : (
              <table className="fin">
                <thead>
                  <tr>
                    <th className="l clickable" onClick={() => toggleEarnSort("fiscal_period")}>Period {sortGlyph("fiscal_period", earnSortKey, earnSortDir)}</th>
                    <th className="clickable" onClick={() => toggleEarnSort("reported_eps")}>Reported EPS {sortGlyph("reported_eps", earnSortKey, earnSortDir)}</th>
                    <th className="clickable" onClick={() => toggleEarnSort("analyst_eps_estimate")}>Est. EPS {sortGlyph("analyst_eps_estimate", earnSortKey, earnSortDir)}</th>
                    <th className="clickable" onClick={() => toggleEarnSort("surprise_pct")}>Surprise {sortGlyph("surprise_pct", earnSortKey, earnSortDir)}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedEarnings.map((e, i) => (
                    <tr key={i}>
                      <td className="l">
                        {e.fiscal_period ?? e.announce_date} {e.is_provisional && <span className="thin-badge">prov.</span>}
                      </td>
                      <td>{e.reported_eps ?? "—"}</td>
                      <td>{e.analyst_eps_estimate ?? "—"}</td>
                      <td className={e.surprise_pct !== null ? (e.surprise_pct >= 0 ? "pos" : "neg") : "mut"}>
                        {e.surprise_pct !== null ? `${e.surprise_pct >= 0 ? "+" : ""}${e.surprise_pct.toFixed(1)}%` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Both valuation charts (trailing-P/E band + CAPE fair-value range),
 * shown only when viewing an INDEX instrument (2026-09-27) -- the SAME
 * two charts and the SAME two lazy-fetched endpoints IndicesPage.tsx's
 * cross-sector tab already uses, so "clicking into an index instrument"
 * and "expanding it from the cross-sector table" show identical data. */
function IndexValuationCharts({
  token, securityId, onUnauthorized,
}: {
  token: string; securityId: string; onUnauthorized: () => void;
}) {
  const [detail, setDetail] = useState<IndexValuationDto | null>(null);
  const [cape, setCape] = useState<CapeDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDetail(null);
    setCape(null);
    setError(null);
    Promise.all([fetchIndexValuationDetail(token, securityId), fetchIndexCapeDetail(token, securityId)])
      .then(([v, c]) => { setDetail(v); setCape(c); })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) { onUnauthorized(); return; }
        setError(err instanceof ApiError ? err.message : "failed to load valuation charts");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, securityId]);

  if (error) return <div className="error" style={{ margin: 14 }}>{error}</div>;
  if (!detail) return <div className="empty-state">Loading valuation…</div>;
  if (!detail.available) return null; // e.g. IDX:SENSEX -- no NSE Indices source, nothing to show

  const showCape = cape?.available && (cape.pre?.available || cape.post?.available);

  // .cols2 (a 2-column grid) ONLY when both charts actually render -- a
  // single card inside it leaves an empty second cell (the same class of
  // bug found and fixed in StockCapeSection, 2026-09-28: an index with no
  // CAPE data, e.g. thin/undefined P/E history, would otherwise show a
  // large blank box next to its P/E-band chart).
  return (
    <div className={showCape ? "cols2" : undefined}>
      <div className="detail-card">
        <div className="detail-card-head">
          <span>Trailing P/E vs own history <FieldHelp term="nifty_valuation_band" /></span>
          <span className="note">
            P/E <b>{detail.pe?.toFixed(2)}×</b>
            {detail.classification && <span className={`valuation-chip ${detail.classification}`} style={{ marginLeft: 8 }}>{detail.classification}</span>}
          </span>
        </div>
        <ValuationBandChart
          series={detail.pe_series}
          meanPe={detail.mean_pe} sdPe={detail.sd_pe}
          currentPe={detail.pe} currentDate={detail.as_of_date}
          classification={detail.classification}
        />
      </div>
      {showCape && (
        <div className="detail-card">
          <div className="detail-card-head">
            <span>CAPE fair-value range <FieldHelp term="cape_fair_value" /></span>
          </div>
          {cape.nominal_note && <div className="valuation-methodology-note">{cape.nominal_note}</div>}
          <CapeChart pre={cape.pre} post={cape.post} />
        </div>
      )}
    </div>
  );
}

/** Relative valuation band, STOCKS only (2026-09-27, extended to 3 tiers
 * 2026-09-28) -- app/factory/stock_cape.py's own module docstrings have
 * the full data check and per-tier methodology. Lazy-fetched on mount,
 * same pattern as IndexValuationCharts. ALWAYS shows the card once loaded
 * (never silently omitted) -- for a stock with too little earnings history
 * for even the loosest tier (~2,084 of ~2,953 stocks, most with ZERO
 * earnings_actuals rows at all), StockCapeChart itself renders the honest
 * "insufficient history" message with the exact reason, rather than the
 * section disappearing as if the feature didn't apply. */
function StockCapeSection({
  token, securityId, onUnauthorized,
}: {
  token: string; securityId: string; onUnauthorized: () => void;
}) {
  const [cape, setCape] = useState<StockCapeDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setCape(null);
    setError(null);
    fetchStockCapeDetail(token, securityId)
      .then(setCape)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) { onUnauthorized(); return; }
        setError(err instanceof ApiError ? err.message : "failed to load the relative valuation band");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, securityId]);

  if (error) return <div className="error" style={{ margin: 14 }}>{error}</div>;
  if (!cape) return null; // still loading -- no flash of an empty state

  // A single full-width card, NOT wrapped in .cols2 (a 2-column grid) --
  // unlike IndexValuationCharts below, there is only ever one chart here,
  // so a 2-column wrapper leaves an empty second cell (a real bug, found
  // 2026-09-28: it rendered as a large blank box to the right of this card
  // on any viewport wide enough for .cols2's 1fr/1fr layout to apply).
  return (
    <div className="detail-card">
      <div className="detail-card-head">
        <span>Relative valuation band <FieldHelp term="stock_relative_cape" /></span>
        {cape.available && cape.classification && (
          <span className="note">
            <span className={`valuation-chip ${cape.classification}`}>{cape.classification}</span>
            {" "}vs this stock's own {cape.tier === "tier3" ? "sector-premium" : "CAPE"} history
          </span>
        )}
      </div>
      {cape.nominal_note && <div className="valuation-methodology-note">{cape.nominal_note}</div>}
      <StockCapeChart data={cape} />
    </div>
  );
}
